import {
    Autorenew, Bookmark, Cancel, CheckCircle, Event, FactCheck, Flag, Handshake, Inbox,
    Paid, Pause, Search, Star, ThumbDown, Verified,
} from '@mui/icons-material';
import { createSvgIcon } from '@mui/material/utils';

// Pipeline-stage icons drawn to match the board's status glyphs.
const TodoIcon = createSvgIcon(
    <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeDasharray="3.2 2.6" />,
    'StatusTodo',
);
const InProgressIcon = createSvgIcon(
    <>
        <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
        <path d="M12 3.5a8.5 8.5 0 0 1 0 17z" fill="currentColor" />
    </>,
    'StatusInProgress',
);

// Icons a status can carry, chosen for the investor pipeline: intake, shortlisting,
// diligence, selection, funding and rejection. The key is what gets stored; keep it in sync
// with the backend whitelist (STATUS_ICON_KEYS in investor-backend dealFlow.service.ts).
export const STATUS_ICONS = {
    inbox: Inbox,                 // new / inbound
    bookmark: Bookmark,           // saved for later
    star: Star,                   // top pick
    search: Search,               // under review
    fact_check: FactCheck,        // due diligence
    event: Event,                 // meeting or pitch scheduled
    handshake: Handshake,         // term sheet / deal agreed
    verified: Verified,           // approved
    paid: Paid,                   // invested / funded
    pause: Pause,                 // on hold
    autorenew: Autorenew,         // in follow-up / negotiation
    flag: Flag,                   // priority
    todo: TodoIcon,               // to do
    in_progress: InProgressIcon,  // in progress
    check_circle: CheckCircle,    // complete
    thumb_down: ThumbDown,        // declined
    cancel: Cancel,               // rejected
};

// Human-readable names, shown as the hover tooltip in the status icon picker.
export const STATUS_ICON_LABELS = {
    inbox: 'New lead',
    bookmark: 'Saved for later',
    star: 'Top pick',
    search: 'Under review',
    fact_check: 'Due diligence',
    event: 'Meeting scheduled',
    handshake: 'Term sheet agreed',
    verified: 'Approved',
    paid: 'Invested',
    pause: 'On hold',
    autorenew: 'Follow-up / negotiating',
    flag: 'Priority',
    todo: 'To do',
    in_progress: 'In progress',
    check_circle: 'Complete',
    thumb_down: 'Declined',
    cancel: 'Rejected',
};

// Built-in statuses start with an icon so the default columns are readable before anyone picks one.
// Keyed by mappedValue (the fixed backend status key). Custom statuses have no default.
const DEFAULT_STATUS_ICONS = {
    invited: 'inbox',        // New
    interested: 'star',      // Shortlisted
    'in-progress': 'handshake', // Selected
    rejected: 'cancel',      // Rejected
};

export const isStatusIcon = (key) => Object.prototype.hasOwnProperty.call(STATUS_ICONS, key);

// The icon key a status shows: its saved icon, or the default when it has never been set.
// A saved '' means the user chose "No icon", so it stays empty.
export const effectiveIconKey = (status) => {
    if (status?.icon !== undefined && status?.icon !== null) return status.icon;
    return DEFAULT_STATUS_ICONS[status?.mappedValue ?? status?.value] || '';
};

// Returns the icon component for a status, or null when it has none (or an unknown key).
export const statusIconComponent = (status) => {
    const key = effectiveIconKey(status);
    return isStatusIcon(key) ? STATUS_ICONS[key] : null;
};
